import { RECURRING_TYPES_MAP } from 'constants/oneOnOne';
import { PERMISSION_CODES } from 'constants/permissions';
import {
  addHours,
  addMonths,
  addWeeks,
  differenceInMonths,
  differenceInWeeks,
  parseJSON,
} from 'date-fns';
import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import {
  getWeekNumberInYear,
  getNumberOfWeeksInYear,
  getNumberOfWeeksInRange,
  getStartOfWeek,
  getEndOfWeek,
  formatDate,
  isFirstWeekOfTheMonth,
  getTimeRangeFromToday,
  getStartEndOfDayUtc,
} from './dateUtils';
import { isObjectEmpty } from './helpers';
import { isEmpty, forbiddenCharacters, validateLength } from './validation';
import { getPersonFullName } from './uiUtils';
import { FILTER_DATE_FORMAT } from '../constants/oneOnOne';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { PARAMS } from '../constants/pages';

export const prepareManageOneOnOneSuccessMessages = (
  successMessages,
  currentRecordId,
  isOneOnOneRequest = false,
  isReschedule = false
) => {
  if (isOneOnOneRequest) {
    return successMessages.general.request;
  }

  if (isReschedule) {
    return successMessages.general.reschedule;
  }

  return currentRecordId
    ? successMessages.general.edit
    : successMessages.general.create;
};

export const prepareDotsMenuItems = (
  labels,
  isCurrentUserModerator,
  isCurrentUserSubject,
  isDisabled,
  canEdit,
  canDelete,
  canManageUserTopic,
  onEdit,
  onDelete,
  onManageTasks,
  onEditEmployeeTopic
) => [
  {
    type: 'edit',
    label: labels.edit,
    shouldItemRender: () => canEdit && !isDisabled,
    action: onEdit,
    icon: EditIcon,
  },
  {
    type: 'manageEmployeeTopic',
    label: labels.manageEmployeeTopic,
    shouldItemRender: () => !isDisabled && canManageUserTopic,
    action: onEditEmployeeTopic,
    icon: EditIcon,
  },
  {
    type: 'manageTasks',
    label: labels.manageTasks,
    shouldItemRender: () =>
      !isDisabled &&
      (canEdit || isCurrentUserModerator || isCurrentUserSubject),
    action: onManageTasks,
    icon: EditIcon,
  },
  {
    type: 'delete',
    label: labels.delete,
    shouldItemRender: () => canDelete,
    action: onDelete,
    icon: DeleteIcon,
  },
];

export const prepareModeratorAgenda = record =>
  record.agenda.filter(agenda => agenda.creator !== record.user?.id);

export const prepareUserAgenda = record =>
  record.agenda.filter(agenda => agenda.creator === record.user?.id);

export const prepareOneOnOneReportWeeks = () => {
  const { MEETING_TIME_START, MEETING_TIME_END } = PARAMS;
  const period = getTimeRangeFromToday();
  const startWeekNumber = getWeekNumberInYear(period.start);
  const startWeekFirstDay = getStartOfWeek(period.start);
  const weeksInYear = getNumberOfWeeksInYear(period.start);
  const totalWeeks = getNumberOfWeeksInRange(period.start, period.end) + 1;
  const weeksArray = Array.from({ length: totalWeeks });
  const weeksList = weeksArray.map((_, index) => {
    const weekNumber =
      startWeekNumber + index > weeksInYear
        ? startWeekNumber + index - weeksInYear
        : startWeekNumber + index;
    const weekStart = formatDate(
      addWeeks(new Date(index === 0 ? period.start : startWeekFirstDay), index),
      FILTER_DATE_FORMAT
    );
    const weekEnd = formatDate(
      index === totalWeeks - 1 ? period.end : getEndOfWeek(new Date(weekStart)),
      FILTER_DATE_FORMAT
    );
    const isFirstMondayInMonth = isFirstWeekOfTheMonth(weekStart);

    return {
      week: weekNumber,
      weekPeriod: {
        [MEETING_TIME_START]: getStartEndOfDayUtc(weekStart),
        [MEETING_TIME_END]: getStartEndOfDayUtc(weekEnd, true),
      },
      isFirstMondayInMonth,
    };
  });

  return {
    startDate: period.start,
    weeksList,
  };
};

export const prepareOneOnOneReportData = (reportResults = []) => {
  const range = prepareOneOnOneReportWeeks();

  return reportResults.reduce((acc, report) => {
    const weeks = range.weeksList.map(currentWeek => {
      const weekReport = report.weeks.find(
        weekResult => weekResult.week === currentWeek.week
      );

      return {
        ...currentWeek,
        impression: weekReport ? Math.round(weekReport.impression) : null,
      };
    });

    return [...acc, { ...report, weeks }];
  }, []);
};

export const isMeetingAccessible = meeting => meeting.is_accessible;

export const prepareNextRecurring = (meetingDateTime, recurringType) => {
  const formattedMeetingDateTime = parseJSON(meetingDateTime);
  const weeksGap = differenceInWeeks(new Date(), formattedMeetingDateTime);
  const monthsGap = differenceInMonths(new Date(), formattedMeetingDateTime);

  switch (recurringType) {
    case RECURRING_TYPES_MAP.WEEKLY.id: {
      return addWeeks(formattedMeetingDateTime, weeksGap + 1);
    }
    case RECURRING_TYPES_MAP.BIWEEKLY.id: {
      return addWeeks(formattedMeetingDateTime, weeksGap + 2);
    }
    case RECURRING_TYPES_MAP.MONTHLY.id: {
      return addMonths(formattedMeetingDateTime, monthsGap + 1);
    }
    default: {
      return null;
    }
  }
};

export const prepareOneOnOneCalendarData = (labels, record) => {
  const { id, meeting_time, targetFullName } = record;
  const startDate = formatDate(meeting_time, 'yyyy-MM-dd');
  const startTime = formatDate(meeting_time, 'HH:mm');
  const endTime = formatDate(addHours(new Date(meeting_time), 1), 'HH:mm');
  const formatedAgenda = [
    ...prepareModeratorAgenda(record),
    ...prepareUserAgenda(record),
  ]
    .map(a => `[br]- ${a.name}`)
    .join('');

  return {
    name: `${labels.name} ${targetFullName}`,
    description: `${labels.greetingMessage} ${targetFullName},[br][br]${labels.description} [br]${formatedAgenda}`,
    startDate,
    startTime,
    endTime,
    uid: id,
    iCalFileName: `${labels.name} ${targetFullName} ${startDate}`,
  };
};

export const prepareOneOnOneRecordsData = (
  records,
  currentUser,
  canAccessUserProfileCheck,
  canAccessDetailsCheck
) =>
  records.map(record => {
    const isCreatedByMe = currentUser.id === record.moderator?.id;
    const isCreatedForMe = currentUser.id === record.user?.id;
    const canAccessOneOnOne = canAccessDetailsCheck(
      record.user?.id,
      PERMISSION_CODES.ONE_ON_ONE_VIEW
    );
    const canAccessModeratorProfile =
      !!record.moderator && canAccessUserProfileCheck(record.moderator?.id);
    const canAccessUserProfile =
      !!record.user && canAccessUserProfileCheck(record.user?.id);

    return {
      ...record,
      meeting_time: record.meeting_time,
      ...(record?.recurring
        ? {
            nextRecurring: prepareNextRecurring(
              record?.meeting_time,
              record?.recurring
            ),
          }
        : {}),
      targetFullName: getPersonFullName(
        isCreatedByMe ? record?.user : record?.moderator
      ),
      note: record.note,
      isCreatedByMe,
      isCreatedForMe,
      canAccessOneOnOne,
      canAccessModeratorProfile,
      canAccessUserProfile,
    };
  });

export const prepareOneOnOneTasksAvailableUsers = (oneOnOneRecord = {}) => {
  const { user, moderator } = oneOnOneRecord;

  return moderator ? Array.of(moderator, user) : Array.of(user);
};

export const isOneOneOneManagerChangedCheck = (oldManager, newManager) =>
  oldManager?.id !== newManager?.id;

export const prepareOneOnOneRequestCardLabels = (
  labels,
  oldManager,
  newManager
) => {
  const isExpired = isOneOneOneManagerChangedCheck(oldManager, newManager);

  if (isObjectEmpty(newManager)) {
    return { ...labels, userRequest: labels.expiredWithNoModerator };
  }

  if (isExpired) {
    return { ...labels, userRequest: labels.expired };
  }

  return labels;
};

export const prepareUserTopicData = (record = {}, currentUserId = null) => {
  const employeeTopic = record?.agenda?.find(
    u => !u.is_recommended && u.creator === currentUserId
  );
  return {
    id: employeeTopic?.id || null,
    meetingId: record?.id || null,
    moderator: getPersonFullName(record?.moderator),
    name: employeeTopic?.name || '',
  };
};

export const prepareUserTopicSidebarTranslations = (
  labels,
  isEdit = false,
  isRequest = false
) => {
  if (isRequest) {
    return {
      ...labels,
      add: labels.requestTitle,
      edit: labels.requestTitle,
      save: isEdit ? labels.save : labels.request,
    };
  }

  return {
    ...labels,
    add: labels.employeeTopicTitle,
    edit: labels.employeeTopicTitle,
  };
};

export const prepareUserTopicFields = () => {
  return [
    {
      name: 'moderator',
      type: FIELD_TYPES.TEXT,
      isDisabled: true,
    },
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      shouldRemoveFieldSpacing: true,
      shouldRemoveFieldError: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 2, 50),
          type: 'topicMaxLength',
        },
      ],
    },
  ];
};
